import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Card } from '@components/atoms/Card';
import { Table } from '@components/atoms/Table';
import { Typography } from '@components/atoms/Typography';
import { RadioButton, RadioButtonType } from '@components/atoms/RadioButton';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useServicesCart } from '@hooks/useServicesCart';
import { useData } from '@hooks/useData';
import { useGetAllStoreSchedulableWorkers } from '@services/useGetAllStoreSchedulableWorkers';
import { useGetServicesCartTotals } from '@services/useGetServicesCartTotals';

const CURRENT_STEP = 2;

export const ServicesBookingEmployees: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { request: requestAllStoreSchedulableWorkers, loading: loadingAllStoreSchedulableWorkers } =
        useGetAllStoreSchedulableWorkers();
    const { request: requestServicesCartTotals } = useGetServicesCartTotals();
    const { login, allStoreSchedulableWorkers } = useData();
    const { storeUuid, initialClickedService, services } = useServicesCart();
    const { t } = useTranslation();

    const translations = {
        title: t('services-booking-employees-component.title'),
        nextButton: t('services-booking-employees-component.next.button'),
        previousButton: t('services-booking-employees-component.previous.button'),
    };

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // request employees, when entering page
    useEffect(() => {
        if (storeUuid) {
            requestServicesCartTotals(
                { uuid: storeUuid || '' },
                {
                    services: services
                        ?.filter((service) => service.checked === true && service.quantity > 0)
                        .map((service) => {
                            return { uuid: service.uuid, quantity: service.quantity };
                        }),
                }
            );

            requestAllStoreSchedulableWorkers({ uuid: storeUuid || '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeUuid]);

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Anchor
                        href={SITE_URLS.servicesBookingScheduling}
                        mode={AnchorMode.primary}
                        disabled={
                            // no services checked
                            services?.find((item) => item.checked === true) === undefined
                        }>
                        {translations.nextButton}
                    </Anchor>

                    <Anchor
                        href={SITE_URLS.servicesCart.replace(':serviceUuid', `${initialClickedService}`)}
                        mode={AnchorMode.primary}
                        outline={true}>
                        {translations.previousButton}
                    </Anchor>
                </div>
            }>
            <>{!loadingAllStoreSchedulableWorkers && allStoreSchedulableWorkers?.data !== null && <WorkersTable />}</>
        </ServicesCartContainer>
    );
};

const WorkersTable: FC = () => {
    const { allStoreSchedulableWorkers } = useData();
    const { worker, setWorker } = useServicesCart();
    const { t } = useTranslation();

    const translations = {
        noneSelected: t('services-booking-employees-component.table.none'),
    };

    const noneSelectedOption = [
        [
            {
                content: (
                    <div className="d-flex align-items-center gap-5">
                        <RadioButton
                            type={RadioButtonType.normal}
                            name="employee"
                            id="noneSelected"
                            value="noneSelected"
                            checked={worker === null || worker.uuid === ''}
                            onChange={() => setWorker(null)}
                        />
                        <div className="align-items-center me-3 my-4">
                            <Typography
                                color="dark"
                                fontSize="14"
                                fontWeight="900"
                                breakpointsStyles={{ md: { fontSize: '18' } }}>
                                {translations.noneSelected}
                            </Typography>
                        </div>
                    </div>
                ),
            },
        ],
        [{ colspan: 2, content: <hr /> }],
    ];

    const employeesRows = (allStoreSchedulableWorkers?.data || []).flatMap((schedulableWorker, index) => {
        return [
            [
                {
                    content: (
                        <div className="d-flex align-items-center gap-5">
                            <RadioButton
                                type={RadioButtonType.normal}
                                name="employee"
                                id={schedulableWorker.uuid || ''}
                                value={schedulableWorker.uuid || ''}
                                checked={worker?.uuid === schedulableWorker.uuid}
                                onChange={() =>
                                    setWorker({ uuid: schedulableWorker.uuid || '', name: schedulableWorker.name || '' })
                                }
                            />
                            <div className="d-flex justify-content-between align-items-center me-3 my-4">
                                <Typography
                                    color="dark"
                                    fontSize="14"
                                    fontWeight="900"
                                    breakpointsStyles={{ md: { fontSize: '18' } }}>
                                    {schedulableWorker.name}
                                </Typography>
                            </div>
                        </div>
                    ),
                },
            ],
            // line separator (except on last line)
            index !== (allStoreSchedulableWorkers?.data?.length || 0) - 1 ? [{ colspan: 4, content: <hr /> }] : [],
        ];
    });

    return (
        <Card>
            <Table borderless={true} hideHeaderOnMobile={true} contentRows={[...noneSelectedOption, ...employeesRows]} />
        </Card>
    );
};
